import { createTheme } from "@mui/material/styles";

const fontStack =
  '"Poppins", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

const sharedTypography = {
  fontFamily: fontStack,
  htmlFontSize: 16,
  h3: { fontFamily: fontStack, fontWeight: 700, fontSize: "1.75rem", lineHeight: 1.25, letterSpacing: "-0.02em" },
  h4: { fontFamily: fontStack, fontWeight: 700, fontSize: "1.5rem", lineHeight: 1.3, letterSpacing: "-0.02em" },
  h5: { fontFamily: fontStack, fontWeight: 600, fontSize: "1.25rem", lineHeight: 1.35 },
  h6: { fontFamily: fontStack, fontWeight: 600, fontSize: "1.0625rem", lineHeight: 1.4 },
  subtitle1: { fontFamily: fontStack, fontSize: "0.875rem", fontWeight: 500, lineHeight: 1.5 },
  subtitle2: { fontFamily: fontStack, fontSize: "0.8125rem", fontWeight: 500, lineHeight: 1.5 },
  body1: { fontFamily: fontStack, fontSize: "0.875rem", lineHeight: 1.55 },
  body2: { fontFamily: fontStack, fontSize: "0.8125rem", lineHeight: 1.5 },
  caption: { fontFamily: fontStack, fontSize: "0.6875rem", lineHeight: 1.43, letterSpacing: "0.02em" },
  button: { fontFamily: fontStack, fontSize: "0.875rem", fontWeight: 600, textTransform: "none" },
};

function buildComponents(palette) {
  const isDark = palette.mode === "dark";
  const border = isDark ? "rgba(255,255,255,0.08)" : "rgba(15, 18, 24, 0.08)";
  const headColor = isDark ? "rgba(240,242,245,0.75)" : "#4a5058";

  return {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          WebkitFontSmoothing: "antialiased",
          MozOsxFontSmoothing: "grayscale",
          scrollBehavior: "smooth",
        },
        body: {
          fontFamily: fontStack,
          backgroundColor: palette.background.default,
          color: palette.text.primary,
        },
        "#root": { minHeight: "100dvh" },
        "::selection": {
          backgroundColor: isDark ? "rgba(212, 168, 74, 0.45)" : "rgba(212, 168, 74, 0.35)",
          color: isDark ? "#fff" : "#1f2329",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: "none" },
        rounded: { boxShadow: isDark ? "0 2px 12px rgba(0,0,0,0.35)" : "0 1px 3px rgba(15, 18, 24, 0.05)" },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 8, boxShadow: "none", textTransform: "none", fontWeight: 600 },
        contained: { "&:hover": { boxShadow: isDark ? "0 4px 16px rgba(0,0,0,0.4)" : "0 4px 14px rgba(15, 18, 24, 0.12)" } },
        containedPrimary: { "&:hover": { boxShadow: "0 4px 14px rgba(87, 191, 120, 0.35)" } },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          bgcolor: isDark ? "rgba(255,255,255,0.04)" : undefined,
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: isDark ? "rgba(255,255,255,0.28)" : "rgba(15, 18, 24, 0.2)",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: isDark ? "rgba(87, 191, 120, 0.55)" : undefined,
          },
        },
        input: { fontFamily: fontStack, color: palette.text.primary },
        notchedOutline: {
          borderColor: isDark ? "rgba(255,255,255,0.14)" : undefined,
        },
      },
    },
    MuiInputLabel: { styleOverrides: { root: { fontFamily: fontStack, color: isDark ? "rgba(240,242,245,0.65)" : undefined } } },
    MuiFormHelperText: { styleOverrides: { root: { fontFamily: fontStack } } },
    MuiDialogTitle: {
      styleOverrides: {
        root: { color: palette.text.primary, fontFamily: fontStack },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: { color: palette.text.secondary, fontFamily: fontStack },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 10, fontFamily: fontStack },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 14,
          fontFamily: fontStack,
          boxShadow: isDark ? "0 20px 60px rgba(0,0,0,0.55)" : "0 16px 48px rgba(15, 18, 24, 0.12)",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontFamily: fontStack, fontWeight: 500 },
        sizeSmall: { fontSize: "0.75rem" },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontFamily: fontStack,
          fontSize: "0.875rem",
          lineHeight: 1.45,
          borderColor: isDark ? "rgba(255,255,255,0.06)" : "rgba(15, 18, 24, 0.06)",
        },
        head: { fontSize: "0.75rem", fontWeight: 700, color: headColor, letterSpacing: "0.04em" },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: { "&:last-child td": { borderBottom: 0 } },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: { transition: "background-color 0.18s ease, color 0.18s ease" },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 10,
          border: `1px solid ${border}`,
          boxShadow: isDark ? "0 12px 40px rgba(0,0,0,0.5)" : "0 8px 28px rgba(15, 18, 24, 0.1)",
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: "0.875rem",
          borderRadius: 6,
          mx: 0.5,
          "&:hover": { bgcolor: "rgba(87, 191, 120, 0.12)" },
        },
      },
    },
  };
}

const adminPresets = {
  light: {
    layout: {
      background: "#f3f5f9",
    },
    topbar: {
      bgcolor: "rgba(255,255,255,0.88)",
      border: "rgba(15, 18, 24, 0.06)",
      shadow: "0 1px 0 rgba(15,18,24,0.04), 0 4px 20px rgba(15,18,24,0.03)",
      menuBtn: {
        color: "#b89230",
        bgcolor: "rgba(212, 168, 74, 0.1)",
        border: "1px solid rgba(212, 168, 74, 0.22)",
        hoverBg: "rgba(212, 168, 74, 0.18)",
      },
    },
    sidebar: {
      background: "#252930",
      border: "rgba(255,255,255,0.07)",
      logoShadow: "drop-shadow(0 4px 12px rgba(0,0,0,0.35))",
      divider: "rgba(255,255,255,0.06)",
      scrollThumb: "rgba(255,255,255,0.14)",
      sectionLabel: "rgba(180,186,198,0.55)",
      iconPillBg: "rgba(255,255,255,0.06)",
      iconPillActiveBg: "rgba(212, 168, 74, 0.22)",
      iconPillBorder: "rgba(255,255,255,0.08)",
      iconPillActiveBorder: "rgba(236, 208, 120, 0.35)",
      accent: "#d4a84a",
      accentLight: "#ecd078",
      accentBorder: "rgba(236, 208, 120, 0.42)",
      goldMuted: "#c9b87a",
      itemText: "rgba(243,245,247,0.88)",
      itemTextActive: "#fffbf0",
      itemIcon: "rgba(240,242,245,0.55)",
      itemIconActive: "#fde9a8",
      childText: "rgba(235,238,243,0.78)",
      childTextActive: "#fff8e6",
      childPanelBg: "rgba(0,0,0,0.16)",
      childPanelBorder: "rgba(255,255,255,0.08)",
      hoverBg: "rgba(255,255,255,0.06)",
      activeBg: "rgba(212, 168, 74, 0.16)",
      activeHoverBg: "rgba(212, 168, 74, 0.22)",
      footerBg: "rgba(0,0,0,0.22)",
      footerBorder: "rgba(255,255,255,0.06)",
      footerMuted: "rgba(180,186,198,0.75)",
    },
    tokens: {
      green: "#57bf78",
      greenDark: "#2d7a47",
      gold: "#d4a84a",
      ink: "#1f2329",
      inkStrong: "#1a1d22",
      inkMuted: "#6a6f79",
      inkSoft: "#8b919c",
      inkFaint: "#9ea3ac",
      border: "rgba(15, 18, 24, 0.08)",
      borderLight: "rgba(15, 18, 24, 0.06)",
      surface: "#ffffff",
      surfaceElevated: "#ffffff",
      canvas: "#f3f5f9",
      panelMuted: "#fafbfc",
      panelHeaderBg: "#f8fafc",
      profileCardHeaderBg: "rgba(87, 191, 120, 0.08)",
      tableHeadBg: "#f6f8fb",
      tableSectionHeadBg: "#f4f6fa",
      listHeaderBg: "#fafbfc",
      tabTrackBg: "#eef0f4",
      rowHover: "rgba(87, 191, 120, 0.04)",
      rowStripe: "rgba(15, 18, 24, 0.015)",
      filterInactive: "rgba(15, 18, 24, 0.05)",
      filterHover: "rgba(15, 18, 24, 0.08)",
      placeholderBg: "rgba(15, 18, 24, 0.03)",
      placeholderBorder: "rgba(15, 18, 24, 0.12)",
      avatarBorder: "#ffffff",
      tabActiveBg: "#1f2329",
      tabActiveHoverBg: "#2a2f38",
      tabInactiveBg: "#ffffff",
      tabInactiveColor: "#5c6067",
      tabActiveColor: "#ffffff",
      successChipBg: "rgba(87, 191, 120, 0.12)",
      successChipColor: "#2d7a47",
      successChipBorder: "rgba(87, 191, 120, 0.35)",
      chipPendingBg: "#efe7bb",
      chipPendingColor: "#ad9a55",
      chipActiveBg: "#dff4e7",
      chipActiveColor: "#5ebb7d",
      chipRejectedBg: "#fde8e8",
      chipRejectedColor: "#c45c5c",
      chipDefaultBg: "#f0f0f0",
      chipDefaultColor: "#666666",
      chipCommercialBg: "#eef3fb",
      chipCommercialColor: "#5a7ab8",
      detailPanelBg: "#fafbfc",
      detailPanelBorder: "#ececf0",
      iconView: "#77a9df",
      iconEdit: "#69c589",
      iconDelete: "#eb9393",
      shadowSm: "0 1px 3px rgba(15, 18, 24, 0.05)",
      shadowMd: "0 8px 32px rgba(15, 18, 24, 0.06)",
      shadowLg: "0 8px 40px rgba(15, 18, 24, 0.06)",
      tableCardRadius: 10,
      radiusSm: 8,
    },
    palette: {
      mode: "light",
      primary: { main: "#57bf78", dark: "#459e63", light: "#7ed49a", contrastText: "#fff" },
      secondary: { main: "#d4a84a", dark: "#b89230", light: "#ecd078", contrastText: "rgba(0,0,0,0.82)" },
      background: { default: "#f3f5f9", paper: "#ffffff" },
      text: { primary: "#1f2329", secondary: "#6a6f79", disabled: "rgba(0,0,0,0.38)" },
      divider: "rgba(15, 18, 24, 0.08)",
      error: { main: "#e06c6c" },
      warning: { main: "#d4a84b" },
      success: { main: "#57bf78" },
      info: { main: "#5b8fd4" },
    },
  },
  dark: {
    layout: {
      background: "#12151a",
    },
    topbar: {
      bgcolor: "rgba(22, 26, 32, 0.92)",
      border: "rgba(255,255,255,0.08)",
      shadow: "0 1px 0 rgba(0,0,0,0.35), 0 4px 24px rgba(0,0,0,0.25)",
      menuBtn: {
        color: "#ecd078",
        bgcolor: "rgba(212, 168, 74, 0.12)",
        border: "1px solid rgba(212, 168, 74, 0.28)",
        hoverBg: "rgba(212, 168, 74, 0.22)",
      },
    },
    sidebar: {
      background: "#161920",
      border: "rgba(255,255,255,0.08)",
      logoShadow: "drop-shadow(0 4px 16px rgba(0,0,0,0.5))",
      divider: "rgba(255,255,255,0.07)",
      scrollThumb: "rgba(255,255,255,0.16)",
      sectionLabel: "rgba(160,168,182,0.5)",
      iconPillBg: "rgba(255,255,255,0.05)",
      iconPillActiveBg: "rgba(212, 168, 74, 0.2)",
      iconPillBorder: "rgba(255,255,255,0.07)",
      iconPillActiveBorder: "rgba(236, 208, 120, 0.32)",
      accent: "#d4a84a",
      accentLight: "#ecd078",
      accentBorder: "rgba(236, 208, 120, 0.35)",
      goldMuted: "#c9b87a",
      itemText: "rgba(235,238,243,0.82)",
      itemTextActive: "#fffbf0",
      itemIcon: "rgba(240,242,245,0.5)",
      itemIconActive: "#fde9a8",
      childText: "rgba(220,224,232,0.72)",
      childTextActive: "#fff8e6",
      childPanelBg: "rgba(0,0,0,0.28)",
      childPanelBorder: "rgba(255,255,255,0.07)",
      hoverBg: "rgba(255,255,255,0.05)",
      activeBg: "rgba(212, 168, 74, 0.14)",
      activeHoverBg: "rgba(212, 168, 74, 0.2)",
      footerBg: "rgba(0,0,0,0.32)",
      footerBorder: "rgba(255,255,255,0.07)",
      footerMuted: "rgba(180,186,198,0.65)",
    },
    tokens: {
      green: "#57bf78",
      greenDark: "#8fd4a8",
      gold: "#d4a84a",
      ink: "#f2f4f7",
      inkStrong: "#ffffff",
      inkMuted: "#c4c9d4",
      inkSoft: "#a8adb8",
      inkFaint: "#8b919c",
      border: "rgba(255,255,255,0.1)",
      borderLight: "rgba(255,255,255,0.06)",
      surface: "#1c2129",
      surfaceElevated: "#232a35",
      canvas: "#12151a",
      panelMuted: "#1a1f27",
      panelHeaderBg: "#232a35",
      profileCardHeaderBg: "rgba(87, 191, 120, 0.1)",
      tableHeadBg: "#232a35",
      tableSectionHeadBg: "#252d38",
      listHeaderBg: "#1f252e",
      tabTrackBg: "#1a1f27",
      rowHover: "rgba(87, 191, 120, 0.08)",
      rowStripe: "rgba(255,255,255,0.02)",
      filterInactive: "rgba(255,255,255,0.06)",
      filterHover: "rgba(255,255,255,0.1)",
      placeholderBg: "rgba(255,255,255,0.04)",
      placeholderBorder: "rgba(255,255,255,0.14)",
      avatarBorder: "#2a3140",
      tabActiveBg: "#d4a84a",
      tabActiveHoverBg: "#c49a42",
      tabInactiveBg: "#252d38",
      tabInactiveColor: "#c4c9d4",
      tabActiveColor: "#1a1d22",
      successChipBg: "rgba(87, 191, 120, 0.18)",
      successChipColor: "#8fd4a8",
      successChipBorder: "rgba(87, 191, 120, 0.4)",
      chipPendingBg: "rgba(224, 184, 92, 0.22)",
      chipPendingColor: "#e8d08a",
      chipActiveBg: "rgba(87, 191, 120, 0.22)",
      chipActiveColor: "#8fd4a8",
      chipRejectedBg: "rgba(240, 128, 128, 0.18)",
      chipRejectedColor: "#f5a8a8",
      chipDefaultBg: "rgba(255,255,255,0.08)",
      chipDefaultColor: "#a8adb8",
      chipCommercialBg: "rgba(91, 143, 212, 0.18)",
      chipCommercialColor: "#9ec5f0",
      detailPanelBg: "#1a1f27",
      detailPanelBorder: "rgba(255,255,255,0.1)",
      iconView: "#9ec5f0",
      iconEdit: "#8fd4a8",
      iconDelete: "#f5a8a8",
      shadowSm: "0 2px 12px rgba(0,0,0,0.35)",
      shadowMd: "0 12px 40px rgba(0,0,0,0.45)",
      shadowLg: "0 12px 48px rgba(0,0,0,0.5)",
      tableCardRadius: 10,
      radiusSm: 8,
    },
    palette: {
      mode: "dark",
      primary: { main: "#57bf78", dark: "#459e63", light: "#7ed49a", contrastText: "#fff" },
      secondary: { main: "#d4a84a", dark: "#b89230", light: "#ecd078", contrastText: "rgba(0,0,0,0.82)" },
      background: { default: "#12151a", paper: "#1c2129" },
      text: { primary: "#f2f4f7", secondary: "#a8adb8", disabled: "rgba(255,255,255,0.38)" },
      divider: "rgba(255,255,255,0.08)",
      error: { main: "#f08080" },
      warning: { main: "#e0b85c" },
      success: { main: "#6fd492" },
      info: { main: "#7eb4e8" },
    },
  },
};

export function createAppTheme(mode = "light") {
  const preset = adminPresets[mode] || adminPresets.light;
  const theme = createTheme({
    palette: preset.palette,
    shape: { borderRadius: 10 },
    typography: sharedTypography,
    breakpoints: {
      values: { xs: 0, sm: 600, md: 900, lg: 1200, xl: 1536 },
    },
    components: buildComponents(preset.palette),
  });

  theme.admin = {
    mode,
    layout: preset.layout,
    topbar: preset.topbar,
    sidebar: preset.sidebar,
    tokens: { ...preset.tokens, tableCardRadius: 10, radiusSm: 8 },
  };

  return theme;
}

export default createAppTheme;
